// ChatWidget.tsx
import React, { useState, useEffect } from 'react';
import { Paper, Box, IconButton, Zoom, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import { colors, fetchThemeColors } from './theme/colors';

// Define types for the message data
type MessageType = 'WIDGET_TOGGLE' | 'WIDGET_RESIZE';

interface ToggleData {
  isOpen: boolean;
}

interface ResizeData {
  width: string | number;
  height: string | number;
}

type MessageData = ToggleData | ResizeData;

// Function to send messages to parent window
const sendMessageToParent = (type: MessageType, data: MessageData): void => {
  if (window.parent) {
    window.parent.postMessage({ type, data }, '*');
  }
};

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [themeColors, setThemeColors] = useState(colors);
  const [clientGuid, setClientGuid] = useState<string | null>(null);
  // Initialize iframeLoaded to true once at the beginning
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Extract client GUID from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guid = params.get('clientGuid');
    
    if (guid) {
      setClientGuid(guid);
      console.log('Client GUID found:', guid);
    } else {
      console.warn('No client GUID provided in URL parameters');
    }
  }, []);

  // Modified effect - only notify parent window when widget state changes, 
  // but keep iframe loaded
  useEffect(() => {
    // Load iframe if not already loaded when chat is opened
    if (isOpen && !iframeLoaded) {
      setIframeLoaded(true);
    }
    
    // Notify the parent window when widget state changes
    sendMessageToParent('WIDGET_TOGGLE', { isOpen });
  }, [isOpen, iframeLoaded]);

  useEffect(() => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const clientGuid = urlParams.get('clientGuid');
    
    if (clientGuid) {
      console.log("Client GUID:", clientGuid);
      // Store it in state or context for use in your app
      setClientGuid(clientGuid);
    }
    
    // Optional: request additional client info from parent
    const requestClientInfo = () => {
      if (window.parent) {
        window.parent.postMessage({ type: 'GET_CLIENT_INFO' }, '*');
      }
    };
    
    // Listen for CLIENT_INFO messages from parent
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;
      
      const { type, data } = event.data;
      if (type === 'CLIENT_INFO') {
        console.log("Received client info:", data);
        // Store client info in state or context
      }
    };
    
    window.addEventListener('message', handleMessage);
    requestClientInfo();
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Initialize theme colors from backend API using client GUID
  useEffect(() => {
    const loadThemeColors = async () => {
      if (clientGuid) {
        try {
          const fetchedColors = await fetchThemeColors(clientGuid);
          setThemeColors(fetchedColors);
        } catch (error) {
          console.error('Failed to fetch theme colors:', error);
          // Fall back to default colors
        }
      }
    };
    
    loadThemeColors();
  }, [clientGuid]);

  // Construct iframe URL with client GUID
  const getIframeUrl = () => {
    const baseUrl = process.env.REACT_APP_DEPLOY_URL + '/chat-ui';
    const url = new URL(baseUrl, window.location.origin);
    
    if (clientGuid) {
      url.searchParams.append('clientGuid', clientGuid);
    }
    
    // Serialize and pass theme colors to avoid duplicate API calls
    url.searchParams.append('themeColors', encodeURIComponent(JSON.stringify(themeColors)));
    
    return url.toString();
  };

  // Send resize message to parent when the component dimensions change
  const handleResize = (width: string | number, height: string | number): void => {
    sendMessageToParent('WIDGET_RESIZE', { width, height });
  };

  // Example: you can call this when your component needs to resize
  // For example, you could add this to a button or in a useEffect

  return (
    <>
      <Zoom in={!isOpen}>
        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'fixed',

            backgroundColor: themeColors.widgetButtonBg,
            color: themeColors.widgetTextColor,
            width: '70px',
            height: '70px',
            fontSize: '1rem',
            padding: '0',
            borderRadius: '50%',
            boxShadow: `0 4px 8px ${themeColors.widgetShadow}`,
            '&:hover': {
              backgroundColor: themeColors.widgetButtonHover,
            },
            zIndex: 9999,
          }}
        >
          {/* <ChatIcon fontSize="large" /> */}
          {themeColors.widgetText}
        </IconButton>
      </Zoom> 

      <Zoom in={isOpen}>
        <Paper
          elevation={4}
          sx={{
            display: isOpen ? 'flex' : 'none',
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '100%',
            height: '100%',
            maxWidth: 'calc(100vw - 30px)',
            maxHeight: 'calc(100vh - 30px)',
            borderRadius: '8px',
            overflow: 'hidden',
            flexDirection: 'column',
            zIndex: 9999,
            boxShadow: `0 4px 12px ${themeColors.widgetShadow}`,
            bgcolor: themeColors.background,
          }}
        >
          {/* Header with close button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: themeColors.widgetHeaderBg,
              color: themeColors.botText,
              borderBottom: `1px solid ${themeColors.border}`,
            }}
          >
            <Typography sx={{ color: themeColors.botText, fontWeight: 'bold' }}>
              {themeColors.headerText}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ 
                color: themeColors.botText,
                '&:hover': {
                  backgroundColor: 'rgba(218, 165, 32, 0.1)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Chat iframe - always keep loaded but hide when not open */}
          <Box sx={{ 
            flex: 1,
            display: 'flex'
          }}>
            <div style={{ 
              width: '100%', 
              height: '100%', 
              display: iframeLoaded ? 'flex' : 'none' 
            }}>
              <iframe
                src={getIframeUrl()}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  border: 'none' 
                }}
                title="Chat"
              />
            </div>
          </Box>
        </Paper>
      </Zoom>
    </>
  );
};

export default ChatWidget;
